import React from 'react';
import { useTranslation } from 'react-i18next';

import type { BlogAuthor, BlogAuthorLink } from '../../../../mock-data/types';
import { Field } from '../../../../components/Field';
import { TextArea } from '../../../../components/TextArea';
import { Icon } from '../../../../components/Icon';
import { Group, GroupLabel, RowFields, RemoveButton } from '../SeoLists/SeoLists.styles';
import { RowsWrap, Row, PairRow, AddButton } from './AuthorField.styles';

/** An author with nothing filled in: what the form shows before anyone types.
 *  A factory rather than a shared constant so no two drafts end up spreading the
 *  same `links` array. */
const emptyAuthor = (): BlogAuthor => ({ name: '', role: '', bio: '', avatarUrl: '', url: '', links: [] });

export type AuthorFieldProps = {
  value: BlogAuthor | undefined;
  onChange: (author: BlogAuthor | undefined) => void;
};

/**
 * The article's byline.
 *
 * The public page shows it twice — the name near the top with a scroll link,
 * the full card at the bottom — and its JSON-LD carries the same person with
 * the social row as `sameAs`. That is the EEAT signal, and it is the one field
 * on the article a generator deliberately does not fill in: putting a name on a
 * piece of writing is an attribution decision a person makes.
 *
 * The fields are ALWAYS on screen. They used to hide behind an "add an author"
 * button, which read as an optional extra and buried the one signal the article
 * cannot generate for itself; an empty form asks the question instead. Nothing
 * is created by showing it: the draft carries no author until the first
 * keystroke, and the page it feeds prefills the byline the owner last used.
 *
 * NAME IS THE SWITCH. An author with a bio and an avatar but no name renders as
 * nothing on the public page (the API mapping drops it outright), so clearing
 * the name here removes the byline rather than leaving a card with nobody in it.
 */
export const AuthorField = ({ value, onChange }: AuthorFieldProps): React.ReactElement => {
  const { t } = useTranslation();

  // The blank stand-in is render-only; `set` promotes it to a real author the
  // moment a field changes.
  const author = value ?? emptyAuthor();

  const set = (patch: Partial<BlogAuthor>): void => onChange({ ...author, ...patch });

  const setLinks = (links: BlogAuthorLink[]): void => set({ links });

  return (
    <Group>
      <GroupLabel text="Author" tag="span" />
      <RowsWrap>
        <Row>
          <PairRow>
            <RowFields>
              <Field
                label={t('Name')}
                value={author.name}
                placeholder="Ana Marin"
                onChange={(name) => set({ name })}
              />
            </RowFields>
            <RowFields>
              <Field
                label={t('Role')}
                value={author.role}
                placeholder="Head of Content"
                onChange={(role) => set({ role })}
              />
            </RowFields>
          </PairRow>
          {/* Empties the whole card in one go, which matters most against a
              prefilled byline: the next article is not always by the same
              person, and clearing five fields by hand to say so is a chore. */}
          <RemoveButton type="button" aria-label={t('Clear the author')} onClick={() => onChange(undefined)}>
            <Icon name="minus" size={16} />
          </RemoveButton>
        </Row>

        <Row>
          <PairRow>
            <RowFields>
              <Field
                label={t('Photo URL')}
                value={author.avatarUrl}
                placeholder="https://…"
                onChange={(avatarUrl) => set({ avatarUrl })}
              />
            </RowFields>
            <RowFields>
              <Field
                label={t('Author page')}
                value={author.url}
                placeholder="https://…"
                onChange={(url) => set({ url })}
              />
            </RowFields>
          </PairRow>
        </Row>
      </RowsWrap>

      <TextArea
        label={t('Bio')}
        value={author.bio}
        rows={3}
        placeholder={t('What makes this person worth reading on this subject')}
        onChange={(bio) => set({ bio })}
      />

      <GroupLabel text="Author links" tag="span" />
      <RowsWrap>
        {author.links.map((link, index) => (
          <Row key={index}>
            <PairRow>
              <RowFields>
                <Field
                  label={t('Label')}
                  value={link.label}
                  placeholder="LinkedIn"
                  onChange={(label) => setLinks(author.links.map((l, i) => (i === index ? { ...l, label } : l)))}
                />
              </RowFields>
              <RowFields>
                <Field
                  label={t('URL')}
                  value={link.url}
                  placeholder="https://…"
                  onChange={(url) => setLinks(author.links.map((l, i) => (i === index ? { ...l, url } : l)))}
                />
              </RowFields>
            </PairRow>
            <RemoveButton
              type="button"
              aria-label={t('Remove entry')}
              onClick={() => setLinks(author.links.filter((_, i) => i !== index))}
            >
              <Icon name="minus" size={16} />
            </RemoveButton>
          </Row>
        ))}
      </RowsWrap>
      <AddButton
        label="Add a link"
        variant="outline"
        icon="plus"
        onClick={() => setLinks([...author.links, { label: '', url: '' }])}
      />
    </Group>
  );
};
